/**
 * Cotações de custo do produto (17 §3.1, D17, D36–D39).
 * - Uma cotação = fornecedor + data + um preço para cada embalagem que ele vende.
 * - O equivalente por unidade-base sempre informa de qual embalagem veio.
 * - Cotação não é alterada: "Revalidar cotação" registra que o preço continua valendo.
 * - Cotação acima do prazo do sistema (ou da validade do fornecedor) gera aviso, sem bloquear.
 */
import { useState } from "react";
import {
  AutocompleteInput,
  Form,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  SelectInput,
  TextField,
  TextInput,
  required,
  useCreate,
  useGetList,
  useGetOne,
  useNotify,
  useRecordContext,
  useRefresh,
  FormDataConsumer,
  SaveButton,
  useCreateSuggestionContext,
  useDataProvider,
  useInput,
} from "react-admin";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextFieldMui from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import VerifiedIcon from "@mui/icons-material/VerifiedOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { EntradaPercentual, vazioParaNulo } from "../componentes";
import { CamposFornecedor } from "./fornecedores";
import { formatarData, formatarDataHora, formatarMoeda, formatarNumero, formatarPercentual, normalizarBusca } from "../lib/formato";
import { supabase } from "../lib/supabase";
import { soDigitos } from "@sobral/dominio";

export interface PrecoCotado {
  id: string;
  cotacao_id: string;
  produto_id: string;
  fornecedor_id: string;
  embalagem_id: string;
  unidade: string;
  embalagem_descricao: string | null;
  fator_base: number;
  custo_prazo: number;
  desconto: number;
  custo_avista: number;
  custo_unit_base: number;
  data_cotacao: string;
  revalidada_em: string | null;
  data_referencia: string;
  idade_dias: number;
  prazo_dias: number | null;
  valida_ate: string | null;
  situacao: "vigente" | "vencida_prazo" | "vencida_validade";
  aliquota_interestadual: number | null;
  frete_tipo: "cif" | "fob" | null;
  frete_valor_unit: number | null;
  observacao: string | null;
}

interface Embalagem {
  id: string;
  unidade: string;
  descricao: string | null;
  fator_base: number;
  base: boolean;
  ativo: boolean;
}


function textoIdade(dias: number) {
  if (dias <= 0) return "Cotada hoje";
  return `Cotada há ${dias} dia${dias === 1 ? "" : "s"}`;
}

/** Situação de uma cotação (registro de v_cotacao_preco no contexto ou passado por props). */
export function ChipSituacaoCotacao({ registro }: { registro?: Pick<PrecoCotado, "situacao" | "idade_dias" | "prazo_dias" | "valida_ate"> }) {
  const doContexto = useRecordContext<PrecoCotado>();
  const r = registro ?? doContexto;
  if (!r) return null;
  if (r.situacao === "vencida_validade") {
    return <Chip size="small" color="error" label={`Validade expirada em ${formatarData(r.valida_ate)}`} />;
  }
  if (r.situacao === "vencida_prazo") {
    return <Chip size="small" color="warning" label={`${textoIdade(r.idade_dias)} · acima do prazo de ${r.prazo_dias} dias`} />;
  }
  return <Chip size="small" variant="outlined" label={textoIdade(r.idade_dias)} />;
}

const ALIQUOTAS = [
  { id: 0.04, name: "4% (importado)" },
  { id: 0.07, name: "7%" },
  { id: 0.12, name: "12%" },
];

const FRETES = [
  { id: "cif", name: "CIF — frete incluso no preço" },
  { id: "fob", name: "FOB — frete por nossa conta" },
];

interface ValoresCotacao {
  fornecedor_id?: string;
  validade_dias?: number | null;
  aliquota_interestadual?: number | null;
  frete_tipo?: "cif" | "fob" | null;
  frete_valor_unit?: number | null;
  observacao?: string | null;
  precos?: { custo_prazo?: number | null; desconto?: number | null }[];
}

function CampoFreteValor() {
  return (
    <FormDataConsumer<ValoresCotacao>>
      {({ formData }) =>
        formData.frete_tipo === "fob" ? (
          <NumberInput
            source="frete_valor_unit"
            label="Frete FOB por unidade-base (R$), se conhecido"
            min={0}
            step={0.01}
            fullWidth
            helperText="Deixe em branco quando o valor ainda não for conhecido; na precificação ele pode ser estimado em percentual."
          />
        ) : null
      }
    </FormDataConsumer>
  );
}

/** Data (yyyy-mm-dd) N dias depois de `base` (padrão: hoje), no calendário local. */
function dataDaquiA(dias: number, base: Date = new Date()): string {
  const d = new Date(base.getTime());
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const PRAZOS_VALIDADE = [15, 30, 60, 90];

/** Validade informada pelo fornecedor, em dias (atalhos 15/30/60/90 ou outro número). */
function ValidadeEmDias({ base }: { base: Date }) {
  const { field } = useInput({ source: "validade_dias" });
  const dias = field.value ? Number(field.value) : null;
  return (
    <Stack spacing={0.5}>
      <TextFieldMui
        label="Validade da cotação (dias)"
        value={field.value ?? ""}
        onChange={(e) => {
          const n = e.target.value.replace(/\D/g, "");
          field.onChange(n === "" ? null : Number(n));
        }}
        inputMode="numeric"
        fullWidth
        helperText={
          dias
            ? `Válida até ${formatarData(dataDaquiA(dias, base))}. Vale o que vencer primeiro: esta data ou o prazo do sistema.`
            : "Opcional. Informe se o fornecedor indicou validade."
        }
      />
      <Stack direction="row" spacing={1}>
        {PRAZOS_VALIDADE.map((p) => (
          <Chip key={p} label={`${p} dias`} size="small" variant={dias === p ? "filled" : "outlined"} onClick={() => field.onChange(p)} />
        ))}
      </Stack>
    </Stack>
  );
}

/**
 * Alíquota interestadual preenchida pela matriz de ICMS (UF do fornecedor → SE). Fornecedor de SE = compra interna.
 * Continua editável (ex.: mercadoria importada a 4%, que o sistema não tem como saber).
 */
function AliquotaAutomatica() {
  const { setValue } = useFormContext();
  const fornecedorId = useWatch({ name: "fornecedor_id" }) as string | undefined;
  const dataProvider = useDataProvider();
  const [origem, setOrigem] = useState<string | null>(null);
  // Fornecedor com que o formulário abriu (correção): mantém a alíquota gravada.
  const fornecedorInicial = useRef(fornecedorId);

  useEffect(() => {
    let ativo = true;
    if (!fornecedorId) {
      setOrigem(null);
      return;
    }
    void (async () => {
      const { data: f } = await dataProvider.getOne("fornecedor", { id: fornecedorId });
      if (!ativo) return;
      const uf = (f as { uf: string }).uf;
      setOrigem(uf);
      if (fornecedorId === fornecedorInicial.current) return;
      if (uf === "SE") {
        setValue("aliquota_interestadual", null, { shouldDirty: true });
        return;
      }
      const { data } = await dataProvider.getList("v_aliquota_icms_vigente", {
        filter: { uf_origem: uf, uf_destino: "SE" },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "uf_origem", order: "ASC" },
      });
      const aliquota = (data[0] as { aliquota: number } | undefined)?.aliquota;
      if (ativo && aliquota !== undefined) setValue("aliquota_interestadual", Number(aliquota), { shouldDirty: true });
    })();
    return () => {
      ativo = false;
    };
  }, [fornecedorId, dataProvider, setValue]);

  // Fornecedor de SE: compra dentro do estado, sem alíquota interestadual (o campo fica travado e vazio).
  if (origem === "SE") {
    return (
      <TextFieldMui
        label="Alíquota interestadual (NF)"
        value="Compra dentro do estado"
        disabled
        fullWidth
        helperText="Fornecedor de SE: sem alíquota interestadual nem complementação de ICMS."
        sx={{ mt: 1, mb: 0.5 }}
      />
    );
  }
  return (
    <SelectInput
      source="aliquota_interestadual"
      label="Alíquota interestadual (NF)"
      choices={ALIQUOTAS}
      emptyText="Compra interna / não informada"
      fullWidth
      helperText={
        origem
          ? `Pela matriz de ICMS: ${origem} → SE. Ajuste se a NF indicar outra (ex.: importado, 4%).`
          : "Preenchida ao escolher o fornecedor."
      }
    />
  );
}

/** Cadastro rápido de fornecedor: começa pelo CNPJ; a consulta à Receita preenche (e corrige) nome, razão social e UF. */
function DialogoFornecedorRapido({
  textoInicial,
  aoFechar,
  aoCriar,
}: {
  textoInicial: string;
  aoFechar: () => void;
  aoCriar: (novo: { id: string }) => void;
}) {
  const [create] = useCreate();
  const notify = useNotify();
  const [erro, setErro] = useState<string | null>(null);
  // Texto digitado na busca: se for um CNPJ, vai para o CNPJ (e consulta sozinho); senão, vira o nome provisório.
  const digitos = soDigitos(textoInicial);
  const ehCnpj = digitos.length === 14;

  async function gravar(valores: Record<string, unknown>) {
    setErro(null);
    try {
      const novo = await create("fornecedor", { data: { ...valores, ativo: true } }, { returnPromise: true });
      notify("Fornecedor cadastrado.", { type: "success" });
      aoCriar(novo as { id: string });
    } catch (e) {
      setErro((e as Error).message.includes("fornecedor_nome_unico") ? "Já existe um fornecedor com este nome." : (e as Error).message);
    }
  }

  return (
    <Dialog open onClose={aoFechar} fullWidth maxWidth="md">
      <ResourceContextProvider value="fornecedor">
        <Form onSubmit={gravar} record={ehCnpj ? { cnpj: digitos, ativo: true } : { nome: textoInicial, ativo: true }}>
          <DialogTitle>Cadastrar fornecedor</DialogTitle>
          <DialogContent>
            {erro && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {erro}
              </Alert>
            )}
            <Box sx={{ pt: 1 }}>
              <CamposFornecedor rapido consultarAoAbrir={ehCnpj} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={aoFechar}>Cancelar</Button>
            <SaveButton label="Cadastrar e usar" alwaysEnable />
          </DialogActions>
        </Form>
      </ResourceContextProvider>
    </Dialog>
  );
}

/** Cadastro pela opção "Cadastrar fornecedor" da lista (texto digitado na busca). */
function CadastroRapidoFornecedor() {
  const { filter, onCancel, onCreate } = useCreateSuggestionContext();
  return <DialogoFornecedorRapido textoInicial={filter ?? ""} aoFechar={onCancel} aoCriar={onCreate} />;
}

/** Fornecedor com busca pelo que se digita (nome, razão social ou CNPJ), botão + para cadastrar pelo CNPJ e cadastro rápido. */
function EntradaFornecedor() {
  const { setValue } = useFormContext();
  const [novo, setNovo] = useState(false);
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ReferenceInput source="fornecedor_id" reference="fornecedor" filter={{ ativo: true }} sort={{ field: "nome", order: "ASC" }} perPage={25}>
          <AutocompleteInput
            optionText={(f: { nome: string; uf: string }) => `${f.nome} (${f.uf})`}
            label="Fornecedor"
            validate={required()}
            fullWidth
            filterToQuery={(texto: string) => (texto ? { "busca@ilike": normalizarBusca(texto) } : {})}
            create={<CadastroRapidoFornecedor />}
            createItemLabel='Cadastrar fornecedor "%{item}"'
            createLabel="Cadastrar novo fornecedor"
          />
        </ReferenceInput>
      </Box>
      <Tooltip title="Novo fornecedor (pelo CNPJ)">
        <Button variant="outlined" onClick={() => setNovo(true)} sx={{ minWidth: 0, px: 1, mt: 1, height: 40 }} aria-label="Novo fornecedor">
          <AddIcon fontSize="small" />
        </Button>
      </Tooltip>
      {novo && (
        <DialogoFornecedorRapido
          textoInicial=""
          aoFechar={() => setNovo(false)}
          aoCriar={(f) => {
            setValue("fornecedor_id", f.id, { shouldDirty: true, shouldValidate: true });
            setNovo(false);
          }}
        />
      )}
    </Stack>
  );
}

/** Formulário de nova cotação: um preço para cada embalagem vendida pelo fornecedor. */

/* ------------------------------ Análise de preços ------------------------------ */

interface LinhaAnalise {
  preco: PrecoCotado;
  anterior: PrecoCotado | null; // cotação anterior do mesmo fornecedor e embalagem
  variacao: number | null; // fração: 0.12 = +12%
  menor: boolean; // menor custo entre as cotações vigentes da embalagem
}

/** Última cotação de cada fornecedor por embalagem, com a variação sobre a anterior e o menor preço vigente. */
function analisar(precos: PrecoCotado[]): Map<string, LinhaAnalise[]> {
  const porEmbalagem = new Map<string, PrecoCotado[]>();
  for (const p of precos) {
    const lista = porEmbalagem.get(p.embalagem_id) ?? [];
    lista.push(p);
    porEmbalagem.set(p.embalagem_id, lista);
  }
  const resultado = new Map<string, LinhaAnalise[]>();
  for (const [embalagem, lista] of porEmbalagem) {
    const porFornecedor = new Map<string, PrecoCotado[]>();
    for (const p of [...lista].sort((a, b) => b.data_cotacao.localeCompare(a.data_cotacao))) {
      const l = porFornecedor.get(p.fornecedor_id) ?? [];
      l.push(p);
      porFornecedor.set(p.fornecedor_id, l);
    }
    const linhas: LinhaAnalise[] = [];
    for (const historico of porFornecedor.values()) {
      const [atual, anterior] = historico;
      if (!atual) continue;
      const variacao = anterior ? Number(atual.custo_avista) / Number(anterior.custo_avista) - 1 : null;
      linhas.push({ preco: atual, anterior: anterior ?? null, variacao, menor: false });
    }
    const vigentes = linhas.filter((l) => l.preco.situacao === "vigente");
    const base = vigentes.length > 0 ? vigentes : linhas;
    const menorValor = Math.min(...base.map((l) => Number(l.preco.custo_avista)));
    for (const l of base) if (Number(l.preco.custo_avista) === menorValor) l.menor = true;
    linhas.sort((a, b) => Number(a.preco.custo_avista) - Number(b.preco.custo_avista));
    resultado.set(embalagem, linhas);
  }
  return resultado;
}

function TextoVariacao({ variacao }: { variacao: number | null }) {
  if (variacao === null) return <span>—</span>;
  if (Math.abs(variacao) < 0.0005) return <span>sem variação</span>;
  const alta = variacao > 0;
  return (
    <Typography component="span" variant="body2" color={alta ? "error.main" : "success.main"} fontWeight={700}>
      {alta ? "▲" : "▼"} {formatarPercentual(Math.abs(variacao))}
    </Typography>
  );
}

function NomeFornecedor({ id }: { id: string }) {
  return (
    <ReferenceField record={{ id: "x", fornecedor_id: id }} source="fornecedor_id" reference="fornecedor" link="edit">
      <TextField source="nome" />
    </ReferenceField>
  );
}

/** Comparação por embalagem: fornecedores lado a lado, menor preço vigente destacado e variação. */
function TabelaAnalise({ precos, embalagens }: { precos: PrecoCotado[]; embalagens: Embalagem[] }) {
  const analise = useMemo(() => analisar(precos), [precos]);
  if (precos.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nenhuma cotação registrada para este produto.
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {embalagens
        .filter((e) => analise.has(e.id))
        .map((e) => {
          const linhas = analise.get(e.id) ?? [];
          return (
            <Box key={e.id}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {e.unidade} {e.base ? "(unidade-base)" : `com ${formatarNumero(e.fator_base)} unidades-base`}
              </Typography>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fornecedor</TableCell>
                      <TableCell align="right">Custo à vista</TableCell>
                      <TableCell align="right">Por unidade-base</TableCell>
                      <TableCell align="right">Variação</TableCell>
                      <TableCell>Cotada em</TableCell>
                      <TableCell>Situação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linhas.map((l) => (
                      <TableRow key={l.preco.id} sx={l.menor ? { bgcolor: "rgba(0, 176, 179, 0.08)" } : undefined}>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <NomeFornecedor id={l.preco.fornecedor_id} />
                            {l.menor && <Chip size="small" color="secondary" label="Menor preço" />}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{formatarMoeda(l.preco.custo_avista)}</TableCell>
                        <TableCell align="right">{formatarMoeda(l.preco.custo_unit_base)}</TableCell>
                        <TableCell align="right">
                          <TextoVariacao variacao={l.variacao} />
                          {l.anterior && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              antes {formatarMoeda(l.anterior.custo_avista)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatarData(l.preco.data_cotacao)}</TableCell>
                        <TableCell>
                          <ChipSituacaoCotacao registro={l.preco} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      <Typography variant="caption" color="text.secondary">
        Considera a cotação mais recente de cada fornecedor. O menor preço é destacado entre as cotações vigentes; a variação
        compara com a cotação anterior do mesmo fornecedor. Volumes diferentes não são comparados entre si.
      </Typography>
    </Stack>
  );
}

/* --------------------------- Registrar / corrigir cotação --------------------------- */

/**
 * Dados para corrigir uma cotação fora da ficha do produto (lápis da precificação): mesmos valores que o
 * "Corrigir" da aba Cotações monta, com as embalagens ativas na ordem do formulário.
 */
export async function carregarCorrecao(cotacaoId: string, produtoId: string): Promise<CorrecaoCotacao> {
  const [pre, emb] = await Promise.all([
    supabase.from("v_cotacao_preco").select("*").eq("cotacao_id", cotacaoId),
    supabase.from("produto_embalagem").select("id, fator_base").eq("produto_id", produtoId).eq("ativo", true).order("fator_base"),
  ]);
  if (pre.error) throw new Error(pre.error.message);
  if (emb.error) throw new Error(emb.error.message);
  const precos = (pre.data ?? []) as PrecoCotado[];
  const cab = precos[0];
  if (!cab) throw new Error("Cotação não encontrada (pode ter sido corrigida ou excluída).");
  return {
    cotacaoId,
    dataCotacao: cab.data_cotacao,
    valores: {
      fornecedor_id: cab.fornecedor_id,
      validade_dias: diasDeValidade(cab.data_cotacao, cab.valida_ate),
      aliquota_interestadual: cab.aliquota_interestadual === null ? null : Number(cab.aliquota_interestadual),
      frete_tipo: cab.frete_tipo,
      frete_valor_unit: cab.frete_valor_unit === null ? null : Number(cab.frete_valor_unit),
      observacao: cab.observacao,
      precos: ((emb.data ?? []) as { id: string }[]).map((e) => {
        const p = precos.find((x) => x.embalagem_id === e.id);
        return p ? { custo_prazo: Number(p.custo_prazo), desconto: Number(p.desconto) } : { custo_prazo: null, desconto: null };
      }),
    },
  };
}

export interface CorrecaoCotacao {
  cotacaoId: string;
  dataCotacao: string;
  valores: ValoresCotacao;
}

/** Comparação ao vivo do preço digitado com o menor vigente da embalagem e com a última cotação do fornecedor. */
function ComparacaoAoVivo({
  indice,
  embalagemId,
  precos,
  ignorarCotacao,
}: {
  indice: number;
  embalagemId: string;
  precos: PrecoCotado[];
  ignorarCotacao?: string;
}) {
  const custoPrazo = useWatch({ name: `precos.${indice}.custo_prazo` }) as number | null | undefined;
  const desconto = useWatch({ name: `precos.${indice}.desconto` }) as number | null | undefined;
  const fornecedorId = useWatch({ name: "fornecedor_id" }) as string | undefined;
  if (!custoPrazo || Number(custoPrazo) <= 0) return null;
  const avista = Number(custoPrazo) * (1 - Number(desconto ?? 0));
  const daEmbalagem = precos.filter((p) => p.embalagem_id === embalagemId && p.cotacao_id !== ignorarCotacao);
  const vigentes = daEmbalagem.filter((p) => p.situacao === "vigente");
  const menor = vigentes.reduce<PrecoCotado | null>((m, p) => (!m || Number(p.custo_avista) < Number(m.custo_avista) ? p : m), null);
  const ultimaDoFornecedor = daEmbalagem
    .filter((p) => p.fornecedor_id === fornecedorId)
    .sort((a, b) => b.data_cotacao.localeCompare(a.data_cotacao))[0];

  const avisos: { texto: string; cor: "error.main" | "success.main" | "text.secondary" }[] = [];
  if (menor) {
    const dif = avista / Number(menor.custo_avista) - 1;
    if (dif > 0.0005) {
      avisos.push({ texto: `${formatarPercentual(dif)} acima do menor preço vigente (${formatarMoeda(menor.custo_avista)})`, cor: "error.main" });
    } else if (dif < -0.0005) {
      avisos.push({ texto: `${formatarPercentual(-dif)} abaixo do menor preço vigente: passa a ser o melhor`, cor: "success.main" });
    } else {
      avisos.push({ texto: "Igual ao menor preço vigente", cor: "text.secondary" });
    }
  }
  if (ultimaDoFornecedor) {
    const dif = avista / Number(ultimaDoFornecedor.custo_avista) - 1;
    if (Math.abs(dif) > 0.0005) {
      avisos.push({
        texto: `${dif > 0 ? "▲" : "▼"} ${formatarPercentual(Math.abs(dif))} em relação à última cotação deste fornecedor (${formatarMoeda(ultimaDoFornecedor.custo_avista)})`,
        cor: dif > 0 ? "error.main" : "success.main",
      });
    }
  }
  return (
    <Box sx={{ pl: { sm: 1 } }}>
      <Typography variant="caption" color="text.secondary" display="block">
        À vista: {formatarMoeda(avista)}
      </Typography>
      {avisos.map((a) => (
        <Typography key={a.texto} variant="caption" color={a.cor} display="block">
          {a.texto}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Formulário de cotação: um preço para cada embalagem vendida pelo fornecedor.
 * Sem `correcao`: registra nova cotação (data e hora automáticas).
 * Com `correcao`: grava a versão corrigida (mesma data da original) e marca a original como substituída.
 */
export function DialogoCotacao({
  aberto,
  aoFechar,
  produtoId,
  correcao,
  aoGravar,
}: {
  aberto: boolean;
  aoFechar: () => void;
  produtoId: string;
  correcao?: CorrecaoCotacao | null;
  /** Recebe o id da cotação gravada (na correção, a versão nova). */
  aoGravar?: (cotacaoId: string) => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const { data: produto } = useGetOne<{ id: string; codigo: string; descricao: string }>("produto", { id: produtoId }, { enabled: aberto });
  const { data: embalagens = [] } = useGetList<Embalagem>(
    "produto_embalagem",
    { filter: { produto_id: produtoId, ativo: true }, pagination: { page: 1, perPage: 100 }, sort: { field: "fator_base", order: "ASC" } },
    { enabled: aberto },
  );
  const { data: precos = [] } = useGetList<PrecoCotado>(
    "v_cotacao_preco",
    { filter: { produto_id: produtoId }, pagination: { page: 1, perPage: 500 }, sort: { field: "data_cotacao", order: "DESC" } },
    { enabled: aberto },
  );
  const base = correcao ? new Date(correcao.dataCotacao) : new Date();

  async function gravar(valores: Record<string, unknown>) {
    const v = valores as ValoresCotacao & { motivo?: string | null };
    setErro(null);
    const lista = embalagens
      .map((e, i) => ({ embalagem: e, preco: v.precos?.[i] }))
      .filter(({ preco }) => preco?.custo_prazo !== undefined && preco?.custo_prazo !== null && Number(preco.custo_prazo) > 0)
      .map(({ embalagem, preco }) => ({ embalagem_id: embalagem.id, custo_prazo: Number(preco?.custo_prazo), desconto: preco?.desconto ?? 0 }));
    if (lista.length === 0) {
      setErro("Informe o preço de pelo menos uma embalagem.");
      return;
    }
    const dados = {
      produto_id: produtoId,
      fornecedor_id: v.fornecedor_id,
      valida_ate: v.validade_dias ? dataDaquiA(Number(v.validade_dias), base) : null,
      aliquota_interestadual: v.aliquota_interestadual ?? null,
      frete_tipo: v.frete_tipo ?? null,
      frete_valor_unit: v.frete_tipo === "fob" ? (v.frete_valor_unit ?? null) : null,
      observacao: v.observacao ?? null,
      precos: lista,
    };
    setGravando(true);
    const { data: gravada, error } = correcao
      ? await supabase.rpc("corrigir_cotacao", { cotacao_id: correcao.cotacaoId, dados, motivo: v.motivo ?? null })
      : await supabase.rpc("registrar_cotacao", { dados });
    setGravando(false);
    if (error) {
      setErro(`Não foi possível gravar a cotação: ${error.message}`);
      return;
    }
    notify(correcao ? "Cotação corrigida. A versão anterior foi mantida no histórico." : "Cotação registrada.", { type: "success" });
    if (typeof gravada === "string") aoGravar?.(gravada);
    refresh();
    aoFechar();
  }

  if (!aberto) return null;
  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="md">
      <Form onSubmit={gravar} record={(correcao?.valores ?? {}) as Record<string, unknown>}>
        <DialogTitle>
          {correcao ? "Corrigir cotação" : "Registrar cotação"}
          {produto && (
            <Typography variant="body2" color="text.secondary">
              {produto.codigo} — {produto.descricao}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {correcao ? (
              <Alert severity="info">
                A versão corrigida mantém a data original ({formatarDataHora(correcao.dataCotacao)}). A versão anterior fica
                registrada no histórico como substituída.
              </Alert>
            ) : (
              <Alert severity="info">
                Informe o preço de cada embalagem que o fornecedor vende. Embalagens sem preço ficam sem cotação. A data e a
                hora são registradas automaticamente.
              </Alert>
            )}
            {erro && <Alert severity="error">{erro}</Alert>}
            <Grid container columnSpacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <EntradaFornecedor />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ValidadeEmDias base={base} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <AliquotaAutomatica />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SelectInput source="frete_tipo" label="Frete" choices={FRETES} emptyText="Não informado" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <CampoFreteValor />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ pt: 1 }}>
              Preços por embalagem
            </Typography>
            {embalagens.map((e, i) => (
              <Grid container columnSpacing={2} key={e.id} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ pt: 1 }}>
                    {e.unidade}
                    {e.descricao ? ` — ${e.descricao}` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {e.base ? "Unidade-base" : `${formatarNumero(e.fator_base)} unidades-base`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <NumberInput source={`precos.${i}.custo_prazo`} label="Custo a prazo (R$)" min={0} step={0.01} fullWidth />
                </Grid>
                <Grid size={{ xs: 6, sm: 2 }}>
                  <EntradaPercentual source={`precos.${i}.desconto`} label="Desconto" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <ComparacaoAoVivo
                    indice={i}
                    embalagemId={e.id}
                    precos={precos}
                    {...(correcao ? { ignorarCotacao: correcao.cotacaoId } : {})}
                  />
                </Grid>
              </Grid>
            ))}
            <TextInput source="observacao" label="Observação" parse={vazioParaNulo} multiline fullWidth />
            {correcao && <TextInput source="motivo" label="Motivo da correção (opcional)" parse={vazioParaNulo} fullWidth />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={aoFechar} disabled={gravando}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={gravando}>
            {gravando ? "Gravando…" : correcao ? "Gravar correção" : "Registrar cotação"}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}

/** Confirma que o preço continua valendo, sem criar nova cotação. */
function DialogoRevalidar({ cotacaoId, aoFechar }: { cotacaoId: string | null; aoFechar: () => void }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [observacao, setObservacao] = useState("");
  const [create, { isPending }] = useCreate();

  function confirmar() {
    if (!cotacaoId) return;
    create(
      "cotacao_revalidacao",
      { data: { cotacao_id: cotacaoId, observacao: observacao.trim() === "" ? null : observacao.trim() } },
      {
        onSuccess: () => {
          notify("Cotação revalidada.", { type: "success" });
          setObservacao("");
          refresh();
          aoFechar();
        },
        onError: (e) => notify(`Não foi possível revalidar: ${(e as Error).message}`, { type: "error" }),
      },
    );
  }

  return (
    <Dialog open={cotacaoId !== null} onClose={isPending ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Revalidar cotação</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            Confirme somente se o fornecedor manteve os preços desta cotação. A data da revalidação e o seu usuário serão
            registrados, e a idade da cotação passará a contar a partir de hoje.
          </Typography>
          <TextFieldMui
            label="Observação (ex.: forma de confirmação)"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            multiline
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={isPending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={confirmar} disabled={isPending} startIcon={<VerifiedIcon />}>
          Confirmar revalidação
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Exclusão com motivo obrigatório: a cotação sai das listas e da análise, e fica no histórico. */
function DialogoExcluir({ cotacaoId, aoFechar }: { cotacaoId: string | null; aoFechar: () => void }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [motivo, setMotivo] = useState("");
  const [gravando, setGravando] = useState(false);

  async function confirmar() {
    if (!cotacaoId || motivo.trim() === "") return;
    setGravando(true);
    const { error } = await supabase.rpc("cancelar_cotacao", { cotacao_id: cotacaoId, motivo: motivo.trim() });
    setGravando(false);
    if (error) {
      notify(`Não foi possível excluir: ${error.message}`, { type: "error" });
      return;
    }
    notify("Cotação excluída. O registro foi mantido no histórico.", { type: "success" });
    setMotivo("");
    refresh();
    aoFechar();
  }

  return (
    <Dialog open={cotacaoId !== null} onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Excluir cotação</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            A cotação deixará de aparecer nas listas, na análise e na precificação. O registro original e o motivo ficam
            guardados no histórico.
          </Typography>
          <TextFieldMui label="Motivo da exclusão" value={motivo} onChange={(e) => setMotivo(e.target.value)} required multiline fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" color="error" onClick={() => void confirmar()} disabled={gravando || motivo.trim() === ""} startIcon={<DeleteIcon />}>
          Excluir cotação
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Dias entre a data da cotação e a validade informada (para reabrir na correção). */
function diasDeValidade(dataCotacao: string, validaAte: string | null): number | null {
  if (!validaAte) return null;
  const inicio = new Date(dataCotacao);
  const [a, m, d] = validaAte.split("-").map(Number);
  const fim = Date.UTC(a ?? 0, (m ?? 1) - 1, d ?? 1);
  const base = Date.UTC(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
  return Math.max(1, Math.round((fim - base) / 86_400_000));
}

/** Aba "Cotações e análise" do produto. */
export function PainelCotacoes() {
  const produto = useRecordContext<{ id: string }>();
  const [registrando, setRegistrando] = useState(false);
  const [correcao, setCorrecao] = useState<CorrecaoCotacao | null>(null);
  const [revalidando, setRevalidando] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const { data: embalagens = [] } = useGetList<Embalagem>(
    "produto_embalagem",
    { filter: { produto_id: produto?.id }, pagination: { page: 1, perPage: 100 }, sort: { field: "fator_base", order: "ASC" } },
    { enabled: Boolean(produto?.id) },
  );
  const { data: precos = [], isPending } = useGetList<PrecoCotado>(
    "v_cotacao_preco",
    { filter: { produto_id: produto?.id }, pagination: { page: 1, perPage: 500 }, sort: { field: "data_cotacao", order: "DESC" } },
    { enabled: Boolean(produto?.id) },
  );
  if (!produto) return null;

  const cotacoes: { id: string; precos: PrecoCotado[] }[] = [];
  for (const p of precos) {
    const grupo = cotacoes.find((c) => c.id === p.cotacao_id);
    if (grupo) grupo.precos.push(p);
    else cotacoes.push({ id: p.cotacao_id, precos: [p] });
  }

  function abrirCorrecao(c: { id: string; precos: PrecoCotado[] }) {
    const cab = c.precos[0];
    if (!cab) return;
    setCorrecao({
      cotacaoId: c.id,
      dataCotacao: cab.data_cotacao,
      valores: {
        fornecedor_id: cab.fornecedor_id,
        validade_dias: diasDeValidade(cab.data_cotacao, cab.valida_ate),
        aliquota_interestadual: cab.aliquota_interestadual === null ? null : Number(cab.aliquota_interestadual),
        frete_tipo: cab.frete_tipo,
        frete_valor_unit: cab.frete_valor_unit === null ? null : Number(cab.frete_valor_unit),
        observacao: cab.observacao,
        precos: embalagens
          .filter((e) => e.ativo)
          .map((e) => {
            const p = c.precos.find((x) => x.embalagem_id === e.id);
            return p ? { custo_prazo: Number(p.custo_prazo), desconto: Number(p.desconto) } : { custo_prazo: null, desconto: null };
          }),
      },
    });
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRegistrando(true)}>
          Registrar cotação
        </Button>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
            Análise de preços
          </Typography>
          {!isPending && <TabelaAnalise precos={precos} embalagens={embalagens} />}
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ fontSize: "1rem" }}>
        Cotações registradas
      </Typography>
      {!isPending &&
        cotacoes.map((c) => {
          const cab = c.precos[0];
          if (!cab) return null;
          return (
            <Card variant="outlined" key={c.id}>
              <CardContent>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      <NomeFornecedor id={cab.fornecedor_id} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cotada em {formatarDataHora(cab.data_cotacao)}
                      {cab.revalidada_em ? ` · revalidada em ${formatarDataHora(cab.revalidada_em)}` : ""}
                      {cab.valida_ate ? ` · validade ${formatarData(cab.valida_ate)}` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Frete:{" "}
                      {cab.frete_tipo === "cif"
                        ? "CIF (incluso)"
                        : cab.frete_tipo === "fob"
                          ? `FOB${cab.frete_valor_unit !== null ? ` — ${formatarMoeda(cab.frete_valor_unit)} por unidade-base` : " — valor a definir"}`
                          : "não informado"}
                      {" · "}Alíquota interestadual:{" "}
                      {cab.aliquota_interestadual !== null ? formatarPercentual(cab.aliquota_interestadual) : "compra interna / não informada"}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                    <ChipSituacaoCotacao registro={cab} />
                    <Button size="small" startIcon={<VerifiedIcon />} onClick={() => setRevalidando(c.id)}>
                      Revalidar
                    </Button>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => abrirCorrecao(c)}>
                      Corrigir
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setExcluindo(c.id)}>
                      Excluir
                    </Button>
                  </Stack>
                </Stack>
                {cab.situacao !== "vigente" && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {cab.situacao === "vencida_validade"
                      ? "A validade informada pelo fornecedor expirou. Recomenda-se confirmar o preço antes de utilizá-lo."
                      : `Esta cotação tem ${cab.idade_dias} dias, acima do prazo de ${cab.prazo_dias} dias definido nos parâmetros. Recomenda-se reavaliar o preço com o fornecedor.`}
                  </Alert>
                )}
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Embalagem</TableCell>
                        <TableCell align="right">Custo a prazo</TableCell>
                        <TableCell align="right">Desconto</TableCell>
                        <TableCell align="right">Custo à vista</TableCell>
                        <TableCell align="right">Por unidade-base</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {c.precos.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>
                            {p.unidade} ({formatarNumero(p.fator_base)})
                          </TableCell>
                          <TableCell align="right">{formatarMoeda(p.custo_prazo)}</TableCell>
                          <TableCell align="right">{formatarPercentual(p.desconto)}</TableCell>
                          <TableCell align="right">{formatarMoeda(p.custo_avista)}</TableCell>
                          <TableCell align="right">
                            {formatarMoeda(p.custo_unit_base)}
                            <Typography variant="caption" color="text.secondary" display="block">
                              pela embalagem {p.unidade}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {cab.observacao && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {cab.observacao}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}

      <DialogoCotacao aberto={registrando} aoFechar={() => setRegistrando(false)} produtoId={produto.id} />
      <DialogoCotacao aberto={correcao !== null} aoFechar={() => setCorrecao(null)} produtoId={produto.id} correcao={correcao} />
      <DialogoRevalidar cotacaoId={revalidando} aoFechar={() => setRevalidando(null)} />
      <DialogoExcluir cotacaoId={excluindo} aoFechar={() => setExcluindo(null)} />
    </Stack>
  );
}
